import { useLanguageContext } from "@/context/LanguageContext";
import type { NotificationModel} from "@/context/NotificationContext";
import { useNotificationContext } from "@/context/NotificationContext";
import { useTimezoneContext } from "@/context/TimezoneContext";
import { Badge, Button, Divider, Popover, Skeleton, Space, Typography } from "antd";
import dayjs from "dayjs";
import { useRouter } from "next/navigation";
import { useCallback, useMemo } from "react";

function NotificationCardContent({ notification, isLast }: { notification: NotificationModel; isLast: boolean }) {
  const router = useRouter();
  const { timezone } = useTimezoneContext();
  const { onRead } = useNotificationContext();
  const { language } = useLanguageContext();

  const description = useMemo(
    () => (notification?.description || "").replaceAll("<b>", "<b/>"),
    [notification?.description],
  );

  const onNotificationClick = useCallback(
    (n: NotificationModel) => {
      if (!n.read) {
        onRead(n._id || "");
      }
    },
    [onRead],
  );

  return (
    <Space
      direction={"vertical"}
      className="gap-0 w-full cursor-pointer"
      onClick={() => {
        onNotificationClick(notification);
        if (notification?.redirect_url_web) {
          if (notification.type === "news") {
            window.open(`/${language}/news/blogs/${notification.redirect_url_web}`, "_blank");
          } else {
            router.push(notification.redirect_url_web);
          }
        }
      }}
    >
      <Space className="justify-between w-full">
        <Typography.Text>{notification.title}</Typography.Text>
        {!notification.read && <Badge status="processing" />}
      </Space>
      <Typography.Text type={"secondary"}>
        <span dangerouslySetInnerHTML={{ __html: description }} />
      </Typography.Text>
      <Typography.Text type={"secondary"}>{dayjs(notification.created_at).tz(timezone.utc).fromNow()}</Typography.Text>
      {!isLast && <Divider style={{ marginTop: "8px", marginBottom: "0" }} />}
    </Space>
  );
}

function NotificationPopoverContent() {
  const { tab, setTab, isLoading, notifications, hasMore, setPage } = useNotificationContext();

  return (
    <Space size={12} direction={"vertical"} className={"w-[250px] sm:w-[400px]"}>
      <Space>
        <Button type={tab === "all" ? "primary" : "text"} onClick={() => setTab("all")}>
          {"All"}
        </Button>
        <Button type={tab === "unread" ? "primary" : "text"} onClick={() => setTab("unread")}>
          {"Unread"}
        </Button>
      </Space>
      <div className="pt-2 pl-2 pr-2 max-h-[500px] overflow-y-auto">
        <Space direction={"vertical"} className="gap-2 w-full">
          <Skeleton active loading={isLoading} paragraph={{ rows: 4 }}>
            {notifications?.map((n, i) => (
              <NotificationCardContent key={i} notification={n} isLast={i === notifications.length - 1} />
            ))}
            {!notifications.length && (
              <Space className="justify-center w-full pt-5 pb-5">
                <Typography.Text className="State-Light">{"You have no notification yet!"}</Typography.Text>
              </Space>
            )}
          </Skeleton>
        </Space>
        {!!hasMore && (
          <Space className="justify-center w-full">
            <Typography.Link onClick={() => setPage(last => last + 1)}>{"Load more"}</Typography.Link>
          </Space>
        )}
      </div>
    </Space>
  );
}

export default function NotificationPopover({ children }: { children: JSX.Element }) {
  return (
    <Popover
      destroyTooltipOnHide
      overlayClassName={"NotificationPopover"}
      showArrow={false}
      placement={"bottomRight"}
      content={NotificationPopoverContent}
      trigger={["click"]}
    >
      {children}
    </Popover>
  );
}
